/**
 * The nomination form's Job Title options, and how a stored or autofilled title maps onto them.
 *
 * The list is `JOB_ROLE_OPTIONS` from `@amzn/awsgcr-form-options` (AWSGCRFormOptions) — the
 * UserCenter / mini-app job-title list (https://account.amazoncloud.cn/config/job-role.json),
 * which the package calls "job role". `value` is the English key and is what gets stored in
 * `guestInformation.jobTitle`; `label` is the zh-CN text shown. Storing the key keeps the value
 * identical to Kunlun / EMS and to what the SFDC job-role mapping reads.
 *
 * Nothing on the wire enforces the list. Customer Link passes through whatever EC sent, and those
 * definitions have changed over time without the history being cleaned, so a contact's title — or
 * an older nomination's free-text one — may match neither a key nor a label. Such a value is kept
 * and shown as given, never blanked: the BD can re-pick, but nothing is erased on their behalf.
 */
import { JOB_ROLE_OPTIONS, type SelectOption } from '@amzn/awsgcr-form-options';

export const JOB_TITLE_OPTIONS: readonly SelectOption[] = JOB_ROLE_OPTIONS;

/** Case- and spacing-insensitive form, so "IT技术经理 " and "it technical manager" both match. */
function norm(s: string): string {
  return s.replace(/\s+/g, '').toLowerCase();
}

/**
 * The stored key for a title: the option whose key or zh-CN label it matches, otherwise the
 * trimmed text as given. Blank reads as absent.
 */
export function canonicalJobTitle(raw: string | undefined): string | undefined {
  const text = raw?.trim();
  if (!text) return undefined;
  const n = norm(text);
  const hit = JOB_TITLE_OPTIONS.find((o) => norm(o.value) === n || norm(o.label) === n);
  return hit ? hit.value : text;
}

/** What to show for a stored title: its zh-CN label when it is a known key, else as stored. */
export function jobTitleLabel(value: string | undefined): string | undefined {
  if (!value) return undefined;
  return JOB_TITLE_OPTIONS.find((o) => o.value === value)?.label ?? value;
}
